import { z } from 'zod';
import { AuditActorTypeSchema } from '@terra/shared-contracts-findings';
import { TargetTypeSchema } from '@terra/shared-schemas-orchestrator';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import {
  NOTIFICATION_CENTER_PAGINATION,
  NOTIFICATION_ENTITY_TYPE,
  NOTIFICATION_KIND,
  NOTIFICATION_RESOLUTION_REASON,
  NOTIFICATION_SOURCE,
  NOTIFICATION_STATUS,
  PLATFORM_NOTIFICATION_ACTION_TYPE,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE,
} from './notification-center.constants';

export const NotificationKindSchema = z.enum([
  NOTIFICATION_KIND.ACTION,
  NOTIFICATION_KIND.HEALTH_ISSUE,
  NOTIFICATION_KIND.CONFIG_GAP,
]);

export const NotificationSourceSchema = z.enum([
  NOTIFICATION_SOURCE.PLATFORM,
  NOTIFICATION_SOURCE.TORCH,
]);

export const NotificationStatusSchema = z.enum([
  NOTIFICATION_STATUS.OPEN,
  NOTIFICATION_STATUS.RESOLVED,
]);

export const NotificationEntityTypeSchema = z.enum([
  NOTIFICATION_ENTITY_TYPE.FINDING,
  NOTIFICATION_ENTITY_TYPE.CREDENTIAL,
  NOTIFICATION_ENTITY_TYPE.DOMAIN,
  NOTIFICATION_ENTITY_TYPE.CONNECTOR,
  NOTIFICATION_ENTITY_TYPE.REPOSITORY,
  NOTIFICATION_ENTITY_TYPE.KNOWLEDGE_SOURCE,
  NOTIFICATION_ENTITY_TYPE.APPLICATION,
  NOTIFICATION_ENTITY_TYPE.MEMORY,
  NOTIFICATION_ENTITY_TYPE.IMPORT_BATCH,
]);

export const NotificationResolutionReasonSchema = z.enum([
  NOTIFICATION_RESOLUTION_REASON.FINDING_STATUS_CHANGED,
  NOTIFICATION_RESOLUTION_REASON.FINDING_UNPUBLISHED,
  NOTIFICATION_RESOLUTION_REASON.REMEDIATION_PLAN_CREATED,
  NOTIFICATION_RESOLUTION_REASON.REMEDIATION_PR_CREATED,
  NOTIFICATION_RESOLUTION_REASON.REMEDIATION_PR_MERGED,
  NOTIFICATION_RESOLUTION_REASON.RETEST_STARTED,
  NOTIFICATION_RESOLUTION_REASON.ENTITY_VIEWED,
  NOTIFICATION_RESOLUTION_REASON.HEALTH_ISSUE_CLEARED,
  NOTIFICATION_RESOLUTION_REASON.CONFIG_GAP_RESOLVED,
  NOTIFICATION_RESOLUTION_REASON.SIGNAL_IMPORT_ACKNOWLEDGED,
]);

export const PlatformNotificationActionTypeSchema = z.enum([
  PLATFORM_NOTIFICATION_ACTION_TYPE.TRIAGE_NEW,
  PLATFORM_NOTIFICATION_ACTION_TYPE.CONTINUE_REMEDIATION,
  PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE,
  PLATFORM_NOTIFICATION_ACTION_TYPE.INVESTIGATE_RETEST_FAILURE,
  PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_APPROVED,
  PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_REJECTED,
  PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY,
  PLATFORM_NOTIFICATION_ACTION_TYPE.NEW_SCHEDULED_REPORT,
  PLATFORM_NOTIFICATION_ACTION_TYPE.BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY,
  PLATFORM_NOTIFICATION_ACTION_TYPE.SIGNAL_IMPORT_COMPLETED,
  PLATFORM_NOTIFICATION_ACTION_TYPE.INITIAL_TESTING_COMPLETE,
]);

export const PlatformNotificationHealthTypeSchema = z.enum([
  PLATFORM_NOTIFICATION_HEALTH_TYPE.CREDENTIAL_VERIFICATION_FAILED,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_APPLICATION_URL,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_ADDITIONAL_DOMAIN,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_OFFLINE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_REQUIRED,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_INTEGRATION_UNREACHABLE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_REPOSITORIES_UNREACHABLE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_CONNECTOR_OFFLINE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.MISSING_API_SPECIFICATION,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.NO_SIGNAL_CATEGORIES_IN_SCOPE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.KNOWLEDGE_SOURCE_SYNC_FAILED,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.CLOUD_IP_UNASSOCIATED,
  PLATFORM_NOTIFICATION_HEALTH_TYPE.ADAPTIVE_SCANNING_PAUSED,
]);

export const PlatformNotificationConfigGapTypeSchema = z.enum([
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_CREDENTIALS_CONFIGURED,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.ALL_CREDENTIALS_DISABLED,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_SOURCE_CODE_INTEGRATION,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_REPOSITORIES_LINKED,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_API_DOCUMENTATION,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_APPLICATION_DOCUMENTATION,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_KNOWLEDGE_BASE_INTEGRATION,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_KNOWLEDGE_SOURCE_PAGES_MAPPED,
  PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE.NO_PREVIOUS_TESTING_REPORTS,
]);

export const NotificationTypeSchema = z.union([
  PlatformNotificationActionTypeSchema,
  PlatformNotificationHealthTypeSchema,
  PlatformNotificationConfigGapTypeSchema,
]);

export const ApplicationNotificationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  targetType: TargetTypeSchema.nullable(),
  targetId: z.string().nullable(),
  kind: NotificationKindSchema,
  source: NotificationSourceSchema,
  type: NotificationTypeSchema,
  entityType: NotificationEntityTypeSchema.nullable(),
  entityId: z.string().nullable(),
  status: NotificationStatusSchema,
  priorityTier: z.int().nullable(),
  severityRank: z.int().nullable(),
  context: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  resolvedAt: z.date().nullable(),
  resolvedByType: AuditActorTypeSchema.nullable(),
  resolvedById: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  resolutionReason: NotificationResolutionReasonSchema.nullable(),
});

export const NotificationTargetQuerySchema = z.object({
  targetType: TargetTypeSchema,
  targetId: z.string(),
});

export const GetApplicationNotificationCenterListQuerySchema = z.object({
  skip: z.coerce.number().int().nonnegative().optional().prefault(0),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(NOTIFICATION_CENTER_PAGINATION)
    .optional()
    .prefault(NOTIFICATION_CENTER_PAGINATION),
});

export const GetApplicationNotificationActionsQuerySchema =
  GetApplicationNotificationCenterListQuerySchema.extend(
    NotificationTargetQuerySchema.shape,
  );

export const GetApplicationNotificationHealthIssuesQuerySchema =
  GetApplicationNotificationCenterListQuerySchema.extend(
    NotificationTargetQuerySchema.shape,
  );

export const GetApplicationConfigCompletenessQuerySchema =
  NotificationTargetQuerySchema;

export const ApplicationNotificationActionsResponseSchema = z.object({
  actions: z.array(ApplicationNotificationSchema),
  count: z.int().nonnegative(),
});

export const ApplicationNotificationHealthIssuesResponseSchema = z.object({
  healthIssues: z.array(ApplicationNotificationSchema),
  count: z.int().nonnegative(),
});

export const GlobalNotificationInboxItemSchema =
  ApplicationNotificationSchema.extend({
    targetName: z.string().nullable(),
    unread: z.boolean(),
  });

export const GlobalInboxKindSchema = z.enum([
  NOTIFICATION_KIND.ACTION,
  NOTIFICATION_KIND.HEALTH_ISSUE,
]);

export const GLOBAL_INBOX_SEARCH_MAX_LENGTH = 200;

export const GetGlobalNotificationInboxQuerySchema = z
  .object({
    cursorCreatedAt: z.iso.datetime().optional(),
    cursorId: MongoObjectIdSchema.optional(),
    limit: z.coerce
      .number()
      .int()
      .positive()
      .max(NOTIFICATION_CENTER_PAGINATION)
      .optional()
      .prefault(NOTIFICATION_CENTER_PAGINATION),
    kind: GlobalInboxKindSchema.optional(),
    targetType: TargetTypeSchema.optional(),
    search: z.string().trim().max(GLOBAL_INBOX_SEARCH_MAX_LENGTH).optional(),
    matchedTypes: z.array(NotificationTypeSchema).optional(),
  })
  .refine(
    (query) =>
      (query.cursorCreatedAt === undefined) === (query.cursorId === undefined),
    {
      message: 'cursorCreatedAt and cursorId must be provided together',
    },
  );

export const GlobalNotificationInboxResponseSchema = z.object({
  items: z.array(GlobalNotificationInboxItemSchema),
  count: z.int().nonnegative(),
  unreadCount: z.int().nonnegative(),
});

export const MarkGlobalInboxSeenResponseSchema = z.object({
  lastSeenAt: z.iso.datetime(),
});

export const ApplicationConfigCompletenessResponseSchema = z.object({
  score: z.int().min(0).max(100),
  gaps: z.array(ApplicationNotificationSchema),
});

export const NotificationIdParamsSchema = z.object({
  notificationId: z.string(),
});

export const MarkEntityViewedBodySchema = z.object({
  entityType: NotificationEntityTypeSchema,
  entityId: z.string(),
  viewedAt: z.iso.datetime().optional(),
});

export const DismissNotificationResponseSchema = z.object({
  success: z.boolean(),
});

export const MarkEntityViewedResponseSchema = z.object({
  resolvedCount: z.number(),
});

export type ApplicationNotification = z.infer<
  typeof ApplicationNotificationSchema
>;
export type GetApplicationNotificationActionsQuery = z.infer<
  typeof GetApplicationNotificationActionsQuerySchema
>;
export type GetApplicationNotificationHealthIssuesQuery = z.infer<
  typeof GetApplicationNotificationHealthIssuesQuerySchema
>;
export type ApplicationNotificationActionsResponse = z.infer<
  typeof ApplicationNotificationActionsResponseSchema
>;
export type ApplicationNotificationHealthIssuesResponse = z.infer<
  typeof ApplicationNotificationHealthIssuesResponseSchema
>;
export type GetGlobalNotificationInboxQuery = z.infer<
  typeof GetGlobalNotificationInboxQuerySchema
>;
export type GlobalInboxKind = z.infer<typeof GlobalInboxKindSchema>;
export type NotificationType = z.infer<typeof NotificationTypeSchema>;
export type GlobalNotificationInboxItem = z.infer<
  typeof GlobalNotificationInboxItemSchema
>;
export type GlobalNotificationInboxResponse = z.infer<
  typeof GlobalNotificationInboxResponseSchema
>;
export type MarkGlobalInboxSeenResponse = z.infer<
  typeof MarkGlobalInboxSeenResponseSchema
>;
export type ApplicationConfigCompletenessResponse = z.infer<
  typeof ApplicationConfigCompletenessResponseSchema
>;
